import { ImportFileType, IUnmanagedBusiness } from '@/coreDomainsModule/entities/unmanagedBusiness';
import { IUnmanagedBusinessRepository } from '../interfaces/IUnmanagedBusinessRepository';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { simpleCopy } from '@/utils/deepCopy';
import { IPagingOptions } from '@/coreDomainsModule/paging';
import { UnmanagedBusinessFilterType } from '@/coreDomainsModule/entities/filter';
import { UnmanagedBusinessService } from '@/coreDomainsModule/services/unmanagedBusinessService';
import { IFilterInfoResponse } from '../interfaces/IFilterInfoRepository';
import { IError } from '../interfaces/IError';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';

/**
 * 未管理の取引の永続化処理を行うクラス
 * LocalStorageを使用する
 */
export class UnmanagedBusinessRepository implements IUnmanagedBusinessRepository {
  private _unmanagedBusinesses: IUnmanagedBusiness[] | null = null;

  //#region プライベート関数

  /**
   * 未管理の取引を取得する
   */
  private _getUnmanagedBusiness = (): IUnmanagedBusiness[] => {
    if (this._unmanagedBusinesses === null) {
      // nullの場合初期化がされていないので初期化（遅延ロード）
      this.initialize();
      if (this._unmanagedBusinesses === null || !Array.isArray(this._unmanagedBusinesses)) {
        this._unmanagedBusinesses = [];
      }
    }
    return this._unmanagedBusinesses;
  };

  /**
   * 初期化処理
   */
  private initialize(): void {
    // localStorageから未管理の取引を取得
    const unmanagedBusinesses = localStorage.getItem('unmanagedBusinesses');
    // 取得した文字列からパース
    if (unmanagedBusinesses) {
      this._unmanagedBusinesses = JSON.parse(unmanagedBusinesses);
    }
  }

  /**
   *　　localStorageに保存する
   */
  private _saveToLocalStorage(): void {
    if (this._unmanagedBusinesses) {
      // localStorageに保存
      localStorage.setItem('unmanagedBusinesses', JSON.stringify(this._unmanagedBusinesses));
    }
  }

  //#endregion

  /**
   * 未管理の取引を保存する
   * @param unmanagedBusiness - 未管理の取引
   */
  save(unmanagedBusiness: IUnmanagedBusiness): INetworkResult<void, Error> {
    this._getUnmanagedBusiness().push(unmanagedBusiness);
    this._saveToLocalStorage();
    return { status: { type: 'success', data: undefined } };
  }

  /**
   * 未管理の取引を一括保存する
   * @param unmanagedBusinesses - 未管理の取引
   */
  bulkSave(unmanagedBusinesses: IUnmanagedBusiness[]): INetworkResult<void, Error> {
    this._getUnmanagedBusiness().push(...unmanagedBusinesses);
    this._saveToLocalStorage();
    return { status: { type: 'success', data: undefined } };
  }

  /**
   * 未管理の取引を全て取得する
   * @param pagingOptions - ページングオプション
   * @param fileType - 取引情報のファイルタイプ
   * @param filterContentItems - フィルターの一覧
   * @param isAsc - ソートの向き
   * @param sortTarget - ソートするカラム
   */
  allGet(
    pagingOptions: IPagingOptions,
    fileType: ImportFileType,
    filterContentItems: FilterContentItem[],
    isAsc: boolean,
    sortTarget?: string
  ): INetworkResult<[IUnmanagedBusiness[], IPagingOptions], Error> {
    // 未管理の取引を取得
    const unmanagedBusinesses = this._getUnmanagedBusiness().filter((business) => business.importFileType === fileType);
    // フィルター処理
    const filteredUnmanagedBusinesses = UnmanagedBusinessService.filterUnmanagedBusinesses(unmanagedBusinesses, filterContentItems);
    // ソート処理
    const sortedUnmanagedBusiness = UnmanagedBusinessService.sortUnmanagedBusinesses(
      filteredUnmanagedBusinesses,
      fileType,
      isAsc,
      sortTarget
    );
    // pagingOptionsを使用してページング処理
    const data = sortedUnmanagedBusiness.slice(pagingOptions.offset, pagingOptions.offset + pagingOptions.pageSize);

    pagingOptions.nextOffset = pagingOptions.offset + pagingOptions.pageSize;
    pagingOptions.totalResultsAvailable = filteredUnmanagedBusinesses.length;
    pagingOptions.returnCount = data.length;
    return { status: { type: 'success', data: [data, pagingOptions] } };
  }

  /**
   * 未管理の取引を取得する
   * @param unmanagedBusinessId - 未管理の取引ID
   */
  get(unmanagedBusinessId: string): INetworkResult<IUnmanagedBusiness | undefined, Error> {
    const targetUnmanagedBusinesses = this._getUnmanagedBusiness().find((business) => business.businessId === unmanagedBusinessId);
    if (!targetUnmanagedBusinesses) {
      return { status: { type: 'error', error: Error('対象の取引が見つかりません') } };
    }
    return { status: { type: 'success', data: simpleCopy(targetUnmanagedBusinesses) } };
  }

  /**
   * 未管理の取引を全て削除する
   */
  deleteAll(): INetworkResult<void, Error> {
    localStorage.removeItem('unmanagedBusinesses');
    this._unmanagedBusinesses = null;
    return { status: { type: 'success', data: undefined } };
  }

  /**
   * 未管理の取引を削除する
   * @param unmanagedBusinessId - 未管理の取引ID
   */
  delete(unmanagedBusinessId: string): INetworkResult<void, Error> {
    const targetIndex = this._getUnmanagedBusiness().findIndex((business) => business.businessId === unmanagedBusinessId);
    if (targetIndex === -1) {
      return { status: { type: 'error', error: Error('対象の取引が見つかりません') } };
    }
    this._getUnmanagedBusiness().splice(targetIndex, 1);
    this._saveToLocalStorage();
    return { status: { type: 'success', data: undefined } };
  }

  /**
   * 未管理の取引をファイルタイプで指定して削除する
   * @param fileTypes - 取引情報のファイルタイプ
   */
  deleteByFileType(fileTypes: ImportFileType[]): INetworkResult<void, Error> {
    // 削除対象以外をフィルタリング
    const keepUnmanagedBusinesses = this._getUnmanagedBusiness().filter((business) => !fileTypes.includes(business.importFileType));

    // 置き換えを行う
    this._unmanagedBusinesses = keepUnmanagedBusinesses;
    this._saveToLocalStorage();
    return { status: { type: 'success', data: undefined } };
  }

  /**
   * 未管理の取引のフィルターを取得する
   * @param pagingOptions - ページングオプション
   * @param filterName - フィルター名
   * @param fileType - 取引情報のファイルタイプ
   * @param keyword - 検索キーワード
   */
  getFilter(
    pagingOptions: IPagingOptions,
    filterName: UnmanagedBusinessFilterType,
    fileType: ImportFileType,
    keyword?: string
  ): INetworkResult<IFilterInfoResponse, IError> {
    const unmanagedBusinesses = this._getUnmanagedBusiness().filter((business) => business.importFileType === fileType);
    let data: string[] | number[] = [];
    switch (filterName) {
      case 'UNMANAGED_BUSINESS_ANNOUNCE_AMOUNT-1':
        data = unmanagedBusinesses.map((business) => UnmanagedBusinessService.calculateAnnounceAmountPerMonth(business, 0));
        break;
      case 'UNMANAGED_BUSINESS_ANNOUNCE_AMOUNT-2':
        data = unmanagedBusinesses.map((business) => UnmanagedBusinessService.calculateAnnounceAmountPerMonth(business, 1));
        break;
      case 'UNMANAGED_BUSINESS_ANNOUNCE_AMOUNT-3':
        data = unmanagedBusinesses.map((business) => UnmanagedBusinessService.calculateAnnounceAmountPerMonth(business, 2));
        break;
      case 'UNMANAGED_BUSINESS_ARRIVAL_AMOUNT':
        data = unmanagedBusinesses.map((business) => business.arrival ?? 0);
        break;
      case 'UNMANAGED_BUSINESS_BUSINESS_NAME':
        data = unmanagedBusinesses.map((business) => business.businessName ?? '');
        break;
      case 'UNMANAGED_BUSINESS_BUSINESS_NUMBER':
        data = unmanagedBusinesses.map((business) => business.businessNumber ?? '');
        break;
      case 'UNMANAGED_BUSINESS_CUSTOMER_NUMBER':
        data = unmanagedBusinesses.map((business) => business.customerNumber ?? '');
        break;
      case 'UNMANAGED_BUSINESS_CUSTOMER_NAME':
        data = unmanagedBusinesses.map((business) => business.customerName ?? '');
        break;
      case 'UNMANAGED_BUSINESS_INVENTORY_AMOUNT':
        data = unmanagedBusinesses.map((business) => business.inventory ?? 0);
        break;
      case 'UNMANAGED_BUSINESS_PACKAGE':
        data = unmanagedBusinesses.map((business) => business.package ?? '');
        break;
      case 'UNMANAGED_BUSINESS_SALES_DESTINATION_NUMBER':
        data = unmanagedBusinesses.map((business) => business.salesDestinationNumber ?? '');
        break;
      case 'UNMANAGED_BUSINESS_SALES_DESTINATION_NAME':
        data = unmanagedBusinesses.map((business) => business.salesDestinationName ?? '');
        break;
      case 'UNMANAGED_BUSINESS_SALES_RESULTS_AMOUNT':
        data = unmanagedBusinesses.map((business) => UnmanagedBusinessService.calculateTotalSalesResult(business));
        break;
      case 'UNMANAGED_BUSINESS_SUPPLIER_NUMBER':
        data = unmanagedBusinesses.map((business) => business.supplierNumber ?? '');
        break;
      case 'UNMANAGED_BUSINESS_SUPPLIER_NAME':
        data = unmanagedBusinesses.map((business) => business.supplierName ?? '');
        break;
      case 'UNMANAGED_BUSINESS_WAREHOUSE_NUMBER':
        data = unmanagedBusinesses.map((business) => business.warehouseNumber ?? '');
        break;
      case 'UNMANAGED_BUSINESS_WAREHOUSE':
        data = unmanagedBusinesses.map((business) => business.warehouse ?? '');
        break;
      case 'UNMANAGED_BUSINESS_END_USER':
        data = unmanagedBusinesses.map((business) => business.endUser ?? '');
        break;
      case 'UNMANAGED_BUSINESS_MANUFACTURER':
        data = unmanagedBusinesses.map((business) => business.manufacturer ?? '');
        break;
    }

    // Stringに統一
    const stringData = data.map((item): string => item.toString());
    // 重複を削除
    const distinctData = Array.from(new Set(stringData));
    // keywordがある場合はフィルタリング
    const searchData = keyword ? distinctData.filter((item) => item.includes(keyword)) : distinctData;
    // pagingOptionsを使用してページング処理
    // NOTE: 現状、フィルター機能はページングを考慮していないないため、コメントアウトしておく
    // const resultData = searchData.slice(pagingOptions.offset, pagingOptions.offset + pagingOptions.pageSize);
    const resultData = searchData;

    return {
      status: {
        type: 'success',
        data: {
          data: resultData,
          pagingOptions: {
            pageSize: pagingOptions.pageSize,
            offset: pagingOptions.offset + pagingOptions.pageSize,
            totalResultsAvailable: resultData.length,
            returnCount: resultData.length,
          },
        },
      },
    };
  }
}
